/**
 * MCP Prefill Hook
 * Handles pre-filling the wizard from URL parameters when source=mcp
 */

interface MealTimings {
    [date: string]: {
        morning: boolean;
        lunch: boolean;
        evening: boolean;
    };
}

interface McpRequest {
    postcode: string;
    addressType: string;
    mealDate: string[];
    mealTimings: MealTimings;
    guests: number;
    budget: number;
    budgetEntry: string;
    cuisines: number[]; // Cuisine IDs (integers)
    dietaryPreferences: string[];
    type: string;
    servingType: string;
    foodPreferences: string;
}

export interface McpPrefill {
    source: 'mcp';
    request: McpRequest;
    completedSteps: string[];
}

interface WizardStep {
    name: string;
    include?: boolean;
    isComplete: boolean;
    [key: string]: unknown;
}

// Steps that should be marked as complete for MCP prefill
// Auth step is excluded as it's where we want the user to land
// diets and preferences are marked complete so submission works after auth
const BASE_STEPS_TO_COMPLETE = [
    'splash',
    'address-type',
    'location',
    'date',
    'meal-timing',
    'guests',
    'event-type',
    'food-type',
    'budget',
    'diets',       // Must be complete for submit to work (not skippable)
    'preferences', // Mark complete to avoid extra step after auth
] as const;

// Additional steps that can be completed if data is provided
const OPTIONAL_COMPLETABLE_STEPS = {
    cuisines: 'cuisines',
} as const;

/**
 * Parse URL parameters for MCP prefill
 * @returns Parsed prefill data or null if not MCP source
 */
export const parseMcpParams = (): McpPrefill | null => {
    if (typeof window === 'undefined') return null;

    const params = new URLSearchParams(window.location.search);

    // Only process if source is mcp
    if (params.get('source') !== 'mcp') return null;

    const postcode = params.get('postcode');
    const mealDate = params.get('mealDate');
    const guests = params.get('guests');
    const budget = params.get('budget');

    // Validate minimum required fields
    if (!postcode || !mealDate || !guests || !budget) {
        console.warn('MCP prefill: Missing required fields (postcode, mealDate, guests, budget)');
        return null;
    }

    // Start with base completed steps
    const completedSteps: string[] = [...BASE_STEPS_TO_COMPLETE];

    // Optional: Cuisines (comma-separated integer IDs)
    const cuisinesParam = params.get('cuisines');
    let parsedCuisines: number[] = [];
    if (cuisinesParam) {
        parsedCuisines = cuisinesParam
            .split(',')
            .map((c) => parseInt(c.trim(), 10))
            .filter((id) => !isNaN(id) && id > 0);

        // If we have valid cuisine IDs, mark cuisines step as complete
        if (parsedCuisines.length > 0) {
            completedSteps.push(OPTIONAL_COMPLETABLE_STEPS.cuisines);
        }
    }

    const prefill: McpPrefill = {
        source: 'mcp',
        request: {
            // Required fields
            postcode,
            addressType: 'Rented venue', // Default for MCP bookings
            mealDate: [mealDate], // Convert to array format
            mealTimings: {
                [mealDate]: { morning: false, lunch: false, evening: true },
            },
            guests: parseInt(guests, 10),
            budget: parseInt(budget, 10),
            budgetEntry: 'total',
            // Optional fields
            cuisines: parsedCuisines,
            dietaryPreferences: [],
            type: '',
            servingType: '',
            foodPreferences: '',
        },
        // Mark completed steps
        completedSteps,
    };

    // Optional: Dietary preferences (comma-separated)
    const dietaryPreferences = params.get('dietaryPreferences');
    if (dietaryPreferences) {
        prefill.request.dietaryPreferences = dietaryPreferences
            .split(',')
            .map((d) => d.trim());
    }

    // Optional: Event type
    const eventType = params.get('eventType');
    if (eventType) {
        prefill.request.type = eventType;
    }

    // Optional: Serving type (food type)
    const servingType = params.get('servingType');
    if (servingType) {
        prefill.request.servingType = servingType;
    }

    // Optional: Food preferences
    const foodPreferences = params.get('foodPreferences');
    if (foodPreferences) {
        prefill.request.foodPreferences = foodPreferences;
    }

    return prefill;
};

/**
 * Get steps marked as complete based on MCP prefill data
 * @param steps - Current steps array
 * @param completedStepNames - Names of steps to mark complete
 * @returns Updated steps array
 */
export const getCompletedSteps = (
    steps: WizardStep[],
    completedStepNames: string[]
): WizardStep[] => {
    return steps.map((step) => {
        if (completedStepNames.includes(step.name)) {
            return {
                ...step,
                isComplete: true,
            };
        }
        return step;
    });
};

/**
 * Find the auth step index
 * @param steps - Steps array
 * @returns Index of auth step
 */
export const getAuthStepIndex = (steps: WizardStep[]): number => {
    return steps.findIndex((step) => step.name === 'auth' && step.include !== false);
};

export default {
    parseMcpParams,
    getCompletedSteps,
    getAuthStepIndex,
};
